<template>
  <div>
    <h1 class="capitalize mb-2">{{ t('sidebar.settings') }}</h1>
    <div class="flex flex-column">
      <label class="mr-2 mb-1 capitalize">{{ t('languageSelectorLabel') }}</label>
      <Select
        v-model="selectedOption"
        :options="languageOptions"
        optionLabel="name"
        :placeholder="t('selectLanguage')"
        pt:option:class="p-2"
        class="w-16rem"
      >
        <template #value="{ value, placeholder }">
          <div v-if="value" class="flex align-items-center">
            <img
              :alt="t('languageFlag', { lang: value.name })"
              :src="value.flag"
              style="width: 23px"
              class="mr-2"
            />
            <div>{{ value.name }}</div>
          </div>
          <span v-else>
            {{ placeholder }}
          </span>
        </template>
        <template #option="{ option }">
          <div class="flex align-items-center">
            <img
              :alt="t('languageFlag', { lang: option.name })"
              :src="option.flag"
              style="width: 23px"
              class="mr-2"
            />
            <div>{{ option.name }}</div>
          </div>
        </template>
      </Select>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useLocaleStore } from '@/store'
import { Language, Role } from '@/opendoors-api/client/types.gen'
import { useI18n } from 'vue-i18n'

definePage({
  name: 'settings',
  meta: {
    requiredRoles: [Role.ADMIN, Role.CUSTOMER]
  }
})

const localeStore = useLocaleStore()
const { t } = useI18n()
const { lang } = storeToRefs(localeStore)

type LanguageOption = { name: string; code: Language; flag: string }
const languageOptions = ref<LanguageOption[]>([
  { name: 'English', code: 'en', flag: '/en.png' },
  { name: 'Dutch', code: 'nl', flag: '/nl.png' },
  { name: 'German', code: 'de', flag: '/de.png' }
])

// So primevue Select 'value' template slot does not pass in the 'option', only the optionValue.
// So we need to do some back 'n forth transformations:
const selectedOption = computed({
  get() {
    return getByCode(lang.value)
  },
  set(val) {
    if (val) {
      localeStore.setLanguage(val.code)
    }
  }
})

const getByCode = (code) => languageOptions.value.find((l) => l.code === code) || null
</script>

<i18n lang="yaml">
en:
  languageSelectorLabel: 'select language'
  selectLanguage: 'select a language'
  languageFlag: '{lang} flag'
nl:
  languageSelectorLabel: 'taal selecteren'
  selectLanguage: 'selecteer een taal'
  languageFlag: '{lang} vlag'
de:
  languageSelectorLabel: 'sprache auswählen'
  selectLanguage: 'sprache auswählen'
  languageFlag: '{lang} flagge'
</i18n>
